import React, { useEffect, useRef, useState } from 'react';

/**
 * PuzzlePiecesReveal — gentle animated wrapper for the CADENCE Promise
 * puzzle visual.
 *
 * Behavior (2026-02 — softened per user feedback):
 *   1) On scroll into view, the artwork eases in from small → large with
 *      a slow fade. No drop, no bounce, no rotation.
 *   2) Two gentle gold light-glows ripple across the TOP TWO quadrants
 *      (LISTEN top-left, INVESTIGATE top-right) in sequence.
 *   3) That's it. No sound. No particle burst. The artwork detail is the
 *      hero — the animation only invites the eye in.
 *
 * Removed (per user feedback):
 *   - All WebAudio "clank" sound effects ("80s Nintendo" complaint)
 *   - The bottom-row pings (TRANSLATE, ADVOCATE)
 *   - The canvas particle "gold bubble" burst at the end
 *
 * Self-contained: pure CSS transitions + a tiny ping overlay.
 */

// Quadrant ping positions as a percentage of the image bounding box.
// Only the TOP TWO pings remain — soft light glows on the upper
// quadrants (the lower two and the final particle burst were removed
// because they distracted from the artwork itself).
const PINGS = [
  { x: 30, y: 38, label: 'listen' },       // LISTEN — top left
  { x: 62, y: 50, label: 'investigate' },  // INVESTIGATE — top right
];

const REVEAL_MS = 1400;       // gentle small-to-large scale-in
const PING_DELAY_MS = 900;    // wait after reveal starts before first ping
const PING_INTERVAL_MS = 520; // between the two soft glows

const PuzzlePiecesReveal = ({ src, alt, className = '', testId, hotspots = [] }) => {
  const wrapperRef = useRef(null);
  const [hasPlayed, setHasPlayed] = useState(false);
  const [imageState, setImageState] = useState('idle'); // idle | revealing | settled
  const [activePings, setActivePings] = useState(new Set());
  // Tap-to-reveal index for mobile/touch (no :hover). Holds the
  // index of the hotspot currently revealing its sub-line; cleared
  // automatically after a short window so the artwork breathes
  // again. Hover on desktop is still pure CSS.
  const [tappedHotspot, setTappedHotspot] = useState(null);
  const tapTimerRef = useRef(null);

  // ===== Master choreography =====
  const runChoreography = () => {
    setActivePings(new Set());
    setImageState('revealing');
    // After the gentle reveal settles, mark as settled (lets the breath loop run)
    setTimeout(() => setImageState('settled'), REVEAL_MS);

    PINGS.forEach((p, i) => {
      setTimeout(() => {
        setActivePings((prev) => {
          const next = new Set(prev);
          next.add(i);
          return next;
        });
        // Auto-clear each ping after its ripple finishes
        setTimeout(() => {
          setActivePings((prev) => {
            const next = new Set(prev);
            next.delete(i);
            return next;
          });
        }, 1100);
      }, PING_DELAY_MS + i * PING_INTERVAL_MS);
    });
  };

  // ===== Trigger on first scroll into view =====
  useEffect(() => {
    if (hasPlayed) return;
    const el = wrapperRef.current;
    if (!el) return;
    const reduced = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      // Honour motion preferences: skip animation entirely.
      setImageState('settled');
      setHasPlayed(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && !hasPlayed) {
            setHasPlayed(true);
            runChoreography();
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0.35 }
    );
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPlayed]);

  // Tap-to-replay
  const handleReplay = (e) => {
    e.stopPropagation();
    if (imageState === 'revealing') return; // mid-flight, ignore
    runChoreography();
  };

  // Tap-to-reveal a hotspot's sub-line on touch devices. Stops
  // propagation so the wrapper's tap-to-replay doesn't fire on the
  // same gesture; auto-clears after 2.6s so the artwork breathes
  // again on its own.
  const handleHotspotTap = (e, idx) => {
    e.stopPropagation();
    setTappedHotspot((prev) => (prev === idx ? null : idx));
    if (tapTimerRef.current) clearTimeout(tapTimerRef.current);
    tapTimerRef.current = setTimeout(() => setTappedHotspot(null), 2600);
  };

  useEffect(() => () => {
    if (tapTimerRef.current) clearTimeout(tapTimerRef.current);
  }, []);

  return (
    <div
      ref={wrapperRef}
      className={`puzzle-reveal puzzle-reveal--${imageState} ${className}`}
      data-testid={testId}
      onClick={handleReplay}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleReplay(e);
        }
      }}
      aria-label="Replay the CADENCE puzzle animation"
    >
      <img
        src={src}
        alt={alt}
        className="puzzle-reveal-img"
        data-testid="pieces-puzzle-visual"
      />

      {/* Two soft top-quadrant glows (LISTEN + INVESTIGATE) */}
      {PINGS.map((p, i) => (
        <span
          key={p.label}
          className={`puzzle-reveal-ping ${activePings.has(i) ? 'is-active' : ''}`}
          style={{ left: `${p.x}%`, top: `${p.y}%` }}
          aria-hidden="true"
          data-ping={p.label}
        />
      ))}

      {/* Word hotspots — invisible hitboxes layered over each of the
          four words on the puzzle artwork (LISTEN, INVESTIGATE,
          TRANSLATE, ADVOCATE). Hover (desktop) or tap (mobile)
          reveals an italic foil sub-line — the artwork itself is
          the interactive surface, per founder spec 2026-02. */}
      {hotspots.length > 0 && (
        <div className="puzzle-reveal-hotspot-layer" aria-hidden="false">
          {hotspots.map((h, idx) => (
            <button
              key={h.label}
              type="button"
              className={`puzzle-reveal-hotspot ${tappedHotspot === idx ? 'is-tapped' : ''}`}
              style={{
                left: `${h.x}%`,
                top: `${h.y}%`,
                width: `${h.w}%`,
                height: `${h.h}%`,
              }}
              onClick={(e) => handleHotspotTap(e, idx)}
              onTouchStart={(e) => handleHotspotTap(e, idx)}
              aria-label={`${h.label} — ${h.subline}`}
              data-testid={`puzzle-hotspot-${h.label.toLowerCase()}`}
            >
              <span className="puzzle-reveal-hotspot-tip" role="tooltip">
                <span className="puzzle-reveal-hotspot-tip-eyebrow">
                  We {h.label.charAt(0) + h.label.slice(1).toLowerCase()}.
                </span>
                <span className="puzzle-reveal-hotspot-tip-line">{h.subline}</span>
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Replay hint after settle */}
      {imageState === 'settled' && (
        <span className="puzzle-reveal-replay-hint" aria-hidden="true">
          Tap to replay
        </span>
      )}
    </div>
  );
};

export default PuzzlePiecesReveal;
